/**
 * Pure helpers of the office requests inbox (tracker item 45, phase 4): labels,
 * the access request evidence, error messages for the 409 codes and the
 * approve / decline bodies. No React and no API imports.
 */
import {
  ApproveRescheduleInput,
  PendingEnrollmentEntry,
  PendingEnrollmentType,
  PortalAccessRequest,
  PortalReviewReason,
  RescheduleRequest,
  RescheduleRequestStatus,
  ScheduleConflict,
  ServiceRequest,
  ServiceRequestSource,
  ServiceRequestStatus,
  ServiceRequestTimeOfDay,
} from '../../api/types';
import { getErrorCode, getErrorDetails, getErrorMessage } from '../../utils/apiError';
import { formatClock } from '../../utils/technician';
import { TIME_OF_DAY_LABELS } from '../../utils/portal';

export const REQUESTS_PATH = '/requests';
export const serviceRequestPath = (id: string) => `/requests/service/${encodeURIComponent(id)}`;

export type RequestsTab = 'service' | 'reschedule' | 'access';
export const REQUEST_TABS: { id: RequestsTab; label: string }[] = [
  { id: 'service', label: 'Service requests' },
  { id: 'reschedule', label: 'Reschedule requests' },
  { id: 'access', label: 'Portal access requests' },
];

export function tabFromParam(value: string | null): RequestsTab {
  return REQUEST_TABS.some((t) => t.id === value) ? (value as RequestsTab) : 'service';
}

export const SERVICE_STATUS_LABELS: Record<ServiceRequestStatus, string> = {
  NEW: 'New',
  CONVERTED: 'Converted to job',
  DECLINED: 'Declined',
};

export const RESCHEDULE_OFFICE_LABELS: Record<RescheduleRequestStatus, string> = {
  OPEN: 'Open',
  APPROVED: 'Approved',
  AUTO_APPROVED: 'Auto-approved',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled by customer',
};

export function statusBadgeClass(status: string): string {
  switch (status) {
    case 'CONVERTED':
    case 'APPROVED':
    case 'AUTO_APPROVED':
    case 'LINKED': return 'bg-green-100 text-green-800';
    case 'DECLINED': return 'bg-red-100 text-red-800';
    case 'CANCELLED': return 'bg-gray-100 text-gray-700';
    default: return 'bg-yellow-100 text-yellow-800';
  }
}

export const SERVICE_FILTERS: { value: ServiceRequestStatus | ''; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'DECLINED', label: 'Declined' },
  { value: '', label: 'All' },
];

/** Where a service request came from (tracker item 49 adds the public web form). */
export const SERVICE_SOURCE_LABELS: Record<ServiceRequestSource, string> = {
  PORTAL: 'Customer portal',
  WEB_FORM: 'Website form',
};

export const SERVICE_SOURCE_FILTERS: { value: ServiceRequestSource | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'PORTAL', label: 'Customer portal' },
  { value: 'WEB_FORM', label: 'Website form' },
];

export const RESCHEDULE_FILTERS: { value: RescheduleRequestStatus | ''; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'AUTO_APPROVED', label: 'Auto-approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: '', label: 'All' },
];

export const ACCESS_FILTERS: { value: 'PENDING' | 'LINKED' | 'DECLINED' | 'ALL'; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'LINKED', label: 'Linked' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'ALL', label: 'All' },
];

/** "10/27/2026" for a timestamp or a date. */
export function whenLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const date = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date.slice(5, 7)}/${date.slice(8, 10)}/${date.slice(0, 4)}` : value;
}

/** "10/27/2026 8:00 AM" (date and an optional HH:MM). */
export function dateTimeLabel(date: string | null, time: string | null): string {
  if (!date) return 'No date';
  return `${whenLabel(date)}${time ? ` ${formatClock(time)}` : ''}`;
}

export function addressLine(a: { address: string; city: string; state: string; zip: string } | null | undefined): string {
  if (!a) return '';
  const region = [a.state, a.zip].filter((v) => v && v.trim()).join(' ');
  return [a.address, a.city, region].filter((v) => v && v.trim()).join(', ');
}

/** Where a service request is for: its property, else the address the customer typed. */
export function requestPlace(request: Pick<ServiceRequest, 'property' | 'serviceAddress'>): string {
  return request.property ? addressLine(request.property) : request.serviceAddress ?? 'No address';
}

export function timeOfDayLabel(value: ServiceRequestTimeOfDay | null): string {
  return value ? TIME_OF_DAY_LABELS[value] : '';
}

export function requestedWhen(request: Pick<ServiceRequest, 'preferredDates' | 'preferredTimeOfDay'>): string {
  const dates = request.preferredDates.map(whenLabel).join(', ');
  const time = request.preferredTimeOfDay && request.preferredTimeOfDay !== 'ANY' ? timeOfDayLabel(request.preferredTimeOfDay) : '';
  return [dates, time].filter(Boolean).join(' · ');
}

export const REVIEW_REASON_LABELS: Record<PortalReviewReason, string> = {
  NO_EMAIL_MATCH: 'No customer has this email address on file.',
  AMBIGUOUS_EMAIL: 'More than one customer has this email address.',
  EMAIL_UNUSABLE: 'The matching customer\'s email is marked unusable.',
  CUSTOMER_HAS_LOGIN: 'The matching customer already has a portal login (knowing an email never reaches an account that has one).',
  TOO_FEW_PROOF_FIELDS: 'Fewer than two of the proof details matched.',
  LOGIN_LIMIT: 'The customer is at the portal login limit.',
  CUSTOMER_LOCKED: 'Too many failed matches were recorded for this customer.',
  CODE_NOT_USABLE: 'The registration code was not usable (wrong, expired or voided).',
};

/** The proof fields of a candidate, each marked matched or not; null when none was checked for it. */
export function evidenceFields(matched: PortalAccessRequest['candidates'][number]['matched']): { key: string; label: string; ok: boolean }[] | null {
  if (!matched) return null;
  return [
    { key: 'address', label: 'Street address', ok: matched.address },
    { key: 'phone', label: 'Phone, last 4 digits', ok: matched.phoneLast4 },
    { key: 'invoice', label: 'Invoice number or amount', ok: matched.invoice },
  ];
}

export function matchedCount(matched: PortalAccessRequest['candidates'][number]['matched']): number {
  return evidenceFields(matched)?.filter((f) => f.ok).length ?? 0;
}

/** The dispatch conflicts on a 409 RESCHEDULE_CONFLICTS (empty for any other error). */
export function conflictsOf(error: unknown): ScheduleConflict[] {
  if (getErrorCode(error) !== 'RESCHEDULE_CONFLICTS') return [];
  const list = getErrorDetails(error)?.conflicts;
  return Array.isArray(list) ? (list as ScheduleConflict[]) : [];
}

/** True when the request changed under the office (decided elsewhere): the list should reload. */
export function isStaleRequestError(error: unknown): boolean {
  const code = getErrorCode(error);
  return code === 'SERVICE_REQUEST_DECIDED' || code === 'RESCHEDULE_REQUEST_NOT_OPEN' || code === 'ALREADY_DECIDED' || code === 'JOB_NOT_RESCHEDULABLE';
}

/** Message of a failed inbox action: the server's, with the "decided already" cases made explicit. */
export function requestActionError(error: unknown, fallback: string): string {
  switch (getErrorCode(error)) {
    case 'SERVICE_REQUEST_DECIDED': return 'This request was already converted or declined by someone else. The list has been refreshed.';
    case 'RESCHEDULE_REQUEST_NOT_OPEN': return 'This request was already answered or cancelled by the customer. The list has been refreshed.';
    case 'ALREADY_DECIDED': return 'This request was already decided by someone else. The list has been refreshed.';
    case 'JOB_NOT_RESCHEDULABLE': return 'The job can no longer be rescheduled (it is no longer scheduled). The list has been refreshed.';
    case 'PORTAL_LOGIN_LIMIT': return `${getErrorMessage(error, 'That customer is at the portal login limit.')} You can raise the limit in Settings > Customer portal.`;
    default: return getErrorMessage(error, fallback);
  }
}

export interface ApprovalDraft {
  date: string;
  /** HH:MM, '' = no start time */
  startTime: string;
  message: string;
}

/** The form's starting values: what the customer asked for (the date, and the time asked for or the current one). */
export function approvalDraft(request: RescheduleRequest): ApprovalDraft {
  return {
    date: request.requestedDate,
    startTime: request.requestedStartTime ?? request.originalStartTime ?? '',
    message: '',
  };
}

/** The approve body: an override only where the office changed what the customer asked for. */
export function approvalBody(request: RescheduleRequest, draft: ApprovalDraft, overrideConflicts: boolean): ApproveRescheduleInput {
  const defaultTime = request.requestedStartTime ?? request.originalStartTime ?? '';
  return {
    ...(draft.date !== request.requestedDate ? { scheduledDate: draft.date } : {}),
    ...(draft.startTime !== defaultTime ? { scheduledStartTime: draft.startTime || null } : {}),
    ...(overrideConflicts ? { overrideConflicts: true } : {}),
    ...(draft.message.trim() ? { message: draft.message.trim() } : {}),
  };
}

export function approvalProblem(draft: ApprovalDraft): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date)) return 'Choose the date to apply';
  if (draft.startTime && !/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.startTime)) return 'Enter the start time as HH:MM';
  if (draft.message.length > 500) return 'The note can be at most 500 characters';
  return null;
}

/** A required decline reason / message: 1 to 500 characters. */
export function requiredTextProblem(value: string, what: string): string | null {
  if (!value.trim()) return `Enter ${what}`;
  if (value.trim().length > 500) return `${what.charAt(0).toUpperCase()}${what.slice(1)} can be at most 500 characters`;
  return null;
}

// ---------------------------------------------------------------- bundle entries (tracker item 85)

/** What the inbox calls a pending enrollment: a customer's request, or a renewal staff created. */
export const BUNDLE_ENTRY_LABELS: Record<PendingEnrollmentType, string> = {
  BUNDLE_REQUEST: 'Bundle request',
  BUNDLE_RENEWAL: 'Bundle renewal',
};

export function bundleEntryClass(type: PendingEnrollmentType): string {
  return type === 'BUNDLE_RENEWAL' ? 'bg-violet-100 text-violet-900' : 'bg-blue-100 text-blue-900';
}

/** The service request filters under which the pending bundle entries still apply (they are all open items). */
export function showsBundleEntries(status: ServiceRequestStatus | '', source: ServiceRequestSource | ''): boolean {
  return (status === 'NEW' || status === '') && source === '';
}

/** `Spring Lawn Program: 3 visits from 03/01/2027` */
export function bundleEntrySummary(entry: Pick<PendingEnrollmentEntry, 'bundleName' | 'stepCount' | 'startDate'>): string {
  return `${entry.bundleName}: ${entry.stepCount} visit${entry.stepCount === 1 ? '' : 's'} from ${whenLabel(entry.startDate)}`;
}
